import { Stack, Typography } from '@mui/material'
import { useState, useEffect, useRef, Fragment } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { TopNavigationInvestor } from '../navigation-top/TopNavigationInvestor'
import OverviewSvg from '~/@ui/_sharedSvgs/ui/Overview.svg?react'
import CoinsSvg from '~/@ui/_sharedSvgs/ui/Coins.svg?react'
import ThreeDotsBubleSvg from '~/@ui/_sharedSvgs/ui/ThreeDotsBuble.svg?react'
import FolderSvg from '~/@ui/_sharedSvgs/ui/Folder.svg?react'
import IdCardBlackSvg from '~/@ui/_sharedSvgs/ui/IdCardBlack.svg?react'
import { OverviewTab } from './OverviewTab/OverviewTab'
import { InvestmentsTab } from './InvestmentsTab/InvestmentsTab'
import { TransactionsTab } from './TransactionsTab/TransactionsTab'
import { WelcomeOverview } from './WelcomeOverview/WelcomeOverview'
import { InfoCard } from '~/@ui/info-card/InfoCard'
import { FirstInvestmentCard } from './FirstInvestmentCard/FirstInvestmentCard'
import DevOnly from '~/@dev/devOnly/DevOnly'
import {
  IInvestmentOrder,
  IInvestorProfileInvestmentOverview,
} from '~/@api/generated-api-types/global.types'
import useOpportunityStore from '../useOpportunityStore/useOpportunityStore'
import { useAlert } from '~/@ui/alert/useAlert'
import { getApi } from '~/@utils/api/getApi'
import { OverviewButton } from '~/@ui/buttons/button-overview/OverviewButton'
import { getErrorMessage } from '~/@utils/api/getErrorMessage'
import { useCurrentUserData } from '~/@biz_account/useCurrentUserData/useCurrentUserData'
import { LoadingWheelOrange } from '~/@ui/info-card/LoadingWheels/LoadingWheelOrange'
import { LoadingWheelPurple } from '~/@ui/info-card/LoadingWheels/LoadingWheelPurple'
import { COLOR_PALLETE } from '~/COLOR_PALLETE'

const TAB_MAPPING = {
  overview: 0,
  investments: 1,
  transactions: 2,
} as const

const INDEX_TO_TAB = {
  0: 'overview',
  1: 'investments',
  2: 'transactions',
} as const

type TabName = keyof typeof TAB_MAPPING

export const PortfolioPage = () => {
  const navigate = useNavigate()
  const { selectedTab } = useParams()

  const [isOpenMore, setIsOpenMore] = useState(false)
  const moreButtonRef = useRef<HTMLDivElement>(null)

  const [investments, setInvestments] = useState<IInvestmentOrder[] | null>(
    null
  )
  const [investmentOverview, setInvestmentOverview] =
    useState<IInvestorProfileInvestmentOverview | null>(null)

  const [devHasInvestment, setDevHasInvestment] = useState(false)

  const { user } = useCurrentUserData()
  const { clearOpportunity } = useOpportunityStore()
  const { showAlert } = useAlert()

  const getTabFromUrl = (): number => {
    const tabParam = selectedTab as TabName | undefined
    return TAB_MAPPING[tabParam ?? 'overview'] ?? 0
  }

  const [tabValue, setTabValue] = useState(getTabFromUrl)

  const handleTabChange = (value: React.SetStateAction<number>) => {
    const newTabValue = typeof value === 'function' ? value(tabValue) : value
    setTabValue(newTabValue)
    const tabName = INDEX_TO_TAB[newTabValue as keyof typeof INDEX_TO_TAB]
    navigate(`/investor/portfolio/${tabName}`, {
      replace: true,
      preventScrollReset: true,
    })
  }

  const getInvestments = async () => {
    try {
      const { data: response } =
        await getApi().investmentOrdersAppApiControllerGetInvestmentOrders()

      if ('success' in response) {
        setInvestments(response.investmentOrders)
        return
      }

      throw new Error('Nasatala neznáma chyba pri načítavaní investícií.')
    } catch (err) {
      console.error(err)
      showAlert('error', getErrorMessage(err))
    }
  }

  const getInvestmentOverview = async () => {
    try {
      const { data: response } =
        await getApi().investorProfileAppApiControllerGetInvestorProfileInvestmentOverview()

      if ('success' in response) {
        setInvestmentOverview(response.investmentOverview)
        return
      }

      throw new Error('Nasatala neznáma chyba pri načítavaní prehladu.')
    } catch (err) {
      console.error(err)
      showAlert('error', getErrorMessage(err))
    }
  }

  // Funkcia pre zatvorenie dropdown menu pri kliknutí mimo
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        moreButtonRef.current &&
        !moreButtonRef.current.contains(event.target as Node)
      ) {
        setIsOpenMore(false)
      }
    }

    if (isOpenMore) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpenMore])

  useEffect(() => {
    setTabValue(getTabFromUrl())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab])

  useEffect(() => {
    clearOpportunity()
    getInvestments()
    getInvestmentOverview()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setDevHasInvestment(Boolean(user?._investorProfile?._investmentOrdersCount))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  return (
    <Stack className="w-full overflow-hidden bg-zBlack200">
      <TopNavigationInvestor user={user} />

      <Stack className="w-full max-w-[768px] self-center gap-8 p-6 pb-8">
        <WelcomeOverview
          firstName={user?._personalInformation?.firstName ?? ''}
        />
        {user && !devHasInvestment && <FirstInvestmentCard />}

        {investments &&
          investments.length > 0 &&
          investments.map((invst) => (
            <Fragment key={`investment-notification-${invst.id}`}>
              {invst.status === 'created' && (
                <InfoCard
                  key={`created-${invst.id}`}
                  headerText="Stav objednávky"
                  id={invst.publicId}
                  bgColor={COLOR_PALLETE.zOrange500}
                  btnColor={COLOR_PALLETE.zOrange400}
                  icon={
                    <LoadingWheelOrange className="flex-shrink-0 w-5 h-5" />
                  }
                  bodyText="Čakáme na dobitie vašich prostriedkov."
                  linkText="Detail objednávky"
                  onClick={() =>
                    navigate(
                      `/investor/portfolio/investments/${invst.publicId}`
                    )
                  }
                />
              )}
              {invst.status === 'pairing' && (
                <InfoCard
                  key={`pairing-${invst.id}`}
                  headerText="Stav objednávky"
                  id={invst.publicId}
                  bgColor={COLOR_PALLETE.zGrey200}
                  btnColor="#DED4FF"
                  icon={
                    <LoadingWheelPurple className="flex-shrink-0 w-5 h-5" />
                  }
                  bodyText="Vašu investíciu začneme párovať so seniorom. Urobíme všetko pre to, aby začala čo najskôr zarábať. Ak sa nám to nepodarí do 60 dní, investíciu vám automaticky vrátime."
                  linkText="Detail objednávky"
                  onClick={() =>
                    navigate(
                      `/investor/portfolio/investments/${invst.publicId}`
                    )
                  }
                />
              )}
            </Fragment>
          ))}
      </Stack>

      <Stack
        className="w-full max-w-[768px] pb-28 mx-auto bg-zWhite100 rounded-t-3xl"
        sx={{ minHeight: 'calc(100vh - 201px)' }}>
        <Typography className="text-xl font-bold text-zBlack200 p-6 pb-[15px]">
          Môj účet
        </Typography>

        <Stack className="w-full flex-row justify-between px-6">
          <Stack className="flex-grow w-1/3">
            <OverviewButton
              onClick={() => handleTabChange(0)}
              isSelected={tabValue === 0}>
              <OverviewSvg />
              Prehľad
            </OverviewButton>
          </Stack>

          <Stack className="flex-grow w-1/3">
            <OverviewButton
              onClick={() => handleTabChange(1)}
              isSelected={tabValue === 1}>
              <CoinsSvg />
              Investície
            </OverviewButton>
          </Stack>

          <Stack ref={moreButtonRef} className="flex-grow w-1/3 relative">
            <OverviewButton
              onClick={() => setIsOpenMore((prev) => !prev)}
              isSelected={tabValue === 2}>
              <ThreeDotsBubleSvg />
              Viac
            </OverviewButton>
            {isOpenMore && (
              <Stack className="absolute top-[52px] right-0 w-[236px] h-[112px] z-10 justify-around ring-inset ring-1 ring-zGrey800 bg-zGrey200 rounded-4d">
                <OverviewButton
                  className="justify-start px-3d"
                  onClick={() => {
                    handleTabChange(2)
                    setIsOpenMore(false)
                  }}>
                  <FolderSvg />
                  Transakcie
                </OverviewButton>
                <OverviewButton
                  className="justify-start px-3d"
                  onClick={() => {
                    navigate('/investor/user-profile')
                  }}>
                  <IdCardBlackSvg />
                  Používateľský profil
                </OverviewButton>
              </Stack>
            )}
          </Stack>
        </Stack>

        {tabValue === 0 && (
          <OverviewTab
            user={user}
            investmentOverview={investmentOverview}
            firstInvestment={investments ? investments?.[0] : null}
          />
        )}
        {tabValue === 1 && <InvestmentsTab investments={investments} />}
        {tabValue === 2 && <TransactionsTab />}
      </Stack>

      <DevOnly>
        <div className="fixed bottom-2 right-2 flex flex-col">
          <span
            className="dev"
            onClick={() => setDevHasInvestment((prev) => !prev)}>
            firstInvestment
          </span>
        </div>
      </DevOnly>
    </Stack>
  )
}
